import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import api from '../utils/api';
import Modal from '../components/Modal';
import MasterSelect from '../components/MasterSelect';
import SeasonSelector from '../components/SeasonSelector';

function fmt(n) {
  return new Intl.NumberFormat('tr-TR', { style: 'currency', currency: 'TRY', maximumFractionDigits: 2 }).format(n || 0);
}

function fmtDate(d) {
  return d ? new Date(d).toLocaleDateString('tr-TR') : '';
}

const STATUS_TABS = [
  { value: '', label: 'Tümü' },
  { value: 'planned', label: '📅 Planlandı' },
  { value: 'active', label: '🔄 Aktif' },
  { value: 'completed', label: '✅ Tamamlandı' },
];

const STATUS_BADGE = {
  planned: { label: 'Planlandı', color: '#3498db' },
  active: { label: 'Aktif', color: '#e67e22' },
  completed: { label: 'Tamamlandı', color: '#27ae60' },
  cancelled: { label: 'İptal', color: '#95a5a6' },
};

const EMPTY_WO = {
  title: '', fieldId: '', workType: '', status: 'planned', seasonId: '',
  plannedDate: new Date().toISOString().split('T')[0],
};

const EMPTY_LABOUR = { workerName: '', workType: '', numWorkers: 1, dailyRate: '', days: 1 };
const EMPTY_MATERIAL = { stockId: '', productName: '', quantityUsed: '', displayUnit: 'kg' };

export default function WorkOrdersPage() {
  const { t } = useTranslation();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [form, setForm] = useState(EMPTY_WO);
  const [fields, setFields] = useState([]);
  const [saving, setSaving] = useState(false);

  // Detail view
  const [detail, setDetail] = useState(null);
  const [showLabourModal, setShowLabourModal] = useState(false);
  const [labourForm, setLabourForm] = useState(EMPTY_LABOUR);
  const [showMaterialModal, setShowMaterialModal] = useState(false);
  const [materialForm, setMaterialForm] = useState(EMPTY_MATERIAL);
  const [stockItems, setStockItems] = useState([]);
  const [showCompleteModal, setShowCompleteModal] = useState(false);
  const [debrief, setDebrief] = useState('');

  const load = useCallback(() => {
    setLoading(true);
    const params = statusFilter ? `?status=${statusFilter}` : '';
    api.get(`/work-orders${params}`).then(r => setItems(r.data.data)).catch(console.error).finally(() => setLoading(false));
  }, [statusFilter]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    api.get('/farm/fields').then(r => setFields(r.data.data)).catch(() => {});
  }, []);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleCreate = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post('/work-orders', form);
      toast.success(t('workOrders.created'));
      setShowCreateModal(false);
      setForm(EMPTY_WO);
      load();
    } catch { toast.error(t('settings.saveFailed')); }
    finally { setSaving(false); }
  };

  const loadDetail = async (id) => {
    try {
      const r = await api.get(`/work-orders/${id}`);
      setDetail(r.data);
    } catch { toast.error('Detay yüklenemedi'); }
  };

  const openDetail = (id) => {
    if (detail?.id === id) { setDetail(null); return; }
    loadDetail(id);
  };

  // Labour
  const addLabour = async (e) => {
    e.preventDefault();
    try {
      await api.post(`/work-orders/${detail.id}/labour`, labourForm);
      toast.success(t('settings.saved'));
      setShowLabourModal(false);
      setLabourForm(EMPTY_LABOUR);
      loadDetail(detail.id);
      load();
    } catch { toast.error(t('settings.saveFailed')); }
  };

  const deleteLabour = async (lid) => {
    try {
      await api.delete(`/work-orders/${detail.id}/labour/${lid}`);
      loadDetail(detail.id);
      load();
    } catch { toast.error(t('settings.deleteFailed')); }
  };

  // Materials
  const openMaterialModal = () => {
    api.get('/farm/stock').then(r => setStockItems(r.data.data)).catch(() => {});
    setShowMaterialModal(true);
  };

  const addMaterial = async (e) => {
    e.preventDefault();
    try {
      await api.post(`/work-orders/${detail.id}/materials`, materialForm);
      toast.success(t('settings.saved'));
      setShowMaterialModal(false);
      setMaterialForm(EMPTY_MATERIAL);
      loadDetail(detail.id);
      load();
    } catch (err) { toast.error(err.response?.data?.error || t('settings.saveFailed')); }
  };

  const deleteMaterial = async (mid) => {
    try {
      await api.delete(`/work-orders/${detail.id}/materials/${mid}`);
      loadDetail(detail.id);
      load();
    } catch { toast.error(t('settings.deleteFailed')); }
  };

  // Complete
  const handleComplete = async () => {
    try {
      await api.post(`/work-orders/${detail.id}/complete`, { debrief });
      toast.success(t('workOrders.completed'));
      setShowCompleteModal(false);
      setDebrief('');
      setDetail(null);
      load();
    } catch { toast.error(t('settings.saveFailed')); }
  };

  const selectStock = (stockId) => {
    const s = stockItems.find(i => i.id === stockId);
    if (s) {
      setMaterialForm(f => ({ ...f, stockId: s.id, productName: s.product_name, displayUnit: s.display_unit || s.unit }));
    }
  };

  return (
    <div>
      <h2 className="section-title">{t('workOrders.title')}</h2>

      <div className="chip-group">
        {STATUS_TABS.map(s => (
          <button key={s.value} className={`chip${statusFilter === s.value ? ' active' : ''}`}
            onClick={() => setStatusFilter(s.value)}>{s.label}</button>
        ))}
      </div>

      <div className="card">
        {loading ? <div className="empty-state"><p>{t('common.loading')}</p></div>
          : items.length === 0 ? (
            <div className="empty-state"><div className="icon">📋</div><p>{t('common.noData')}</p></div>
          ) : items.map(item => (
            <div key={item.id} className="txn-item" style={{ flexDirection: 'column', alignItems: 'stretch' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', cursor: 'pointer' }}
                onClick={() => openDetail(item.id)}>
                <div className="txn-main">
                  <div className="txn-name">{item.title}</div>
                  <div className="txn-meta">
                    {fmtDate(item.planned_date)}
                    {item.field_name && ` · ${item.field_name}`}
                    {` · ${item.work_type}`}
                  </div>
                  <span style={{
                    display: 'inline-block', padding: '2px 8px', borderRadius: 6,
                    fontSize: '0.7rem', fontWeight: 700, marginTop: 4,
                    background: STATUS_BADGE[item.status]?.color || '#999', color: '#fff',
                  }}>
                    {STATUS_BADGE[item.status]?.label || item.status}
                  </span>
                </div>
                <span className="txn-amount expense">{fmt(item.total_cost)}</span>
              </div>

              {/* Detail panel */}
              {detail?.id === item.id && (
                <div style={{ marginTop: 10, padding: 10, background: '#f9f9f9', borderRadius: 8 }}>
                  {/* Labour */}
                  <div style={{ marginBottom: 10 }}>
                    <div style={{ fontWeight: 700, fontSize: '0.85rem', marginBottom: 4 }}>
                      {t('workOrders.labour')} ({fmt(detail.total_labour_cost)})
                    </div>
                    {(detail.labour || []).map(l => (
                      <div key={l.id} style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.8rem', marginBottom: 2 }}>
                        <span>{l.worker_name} · {l.num_workers} kişi · {l.days} gün</span>
                        <div>
                          <span style={{ fontWeight: 700, marginRight: 8 }}>{fmt(l.total_amount)}</span>
                          {item.status !== 'completed' && (
                            <button onClick={() => deleteLabour(l.id)} style={{ background: 'none', border: 'none', color: '#c00', cursor: 'pointer' }}>✕</button>
                          )}
                        </div>
                      </div>
                    ))}
                    {item.status !== 'completed' && (
                      <button onClick={() => setShowLabourModal(true)}
                        style={{ background: 'var(--green-pale)', border: 'none', borderRadius: 6, padding: '4px 10px',
                          fontSize: '0.75rem', fontWeight: 700, cursor: 'pointer', color: 'var(--green-mid)', marginTop: 4 }}>
                        ➕ {t('workOrders.addLabour')}
                      </button>
                    )}
                  </div>

                  {/* Materials */}
                  <div style={{ marginBottom: 10 }}>
                    <div style={{ fontWeight: 700, fontSize: '0.85rem', marginBottom: 4 }}>
                      {t('workOrders.materials')} ({fmt(detail.total_material_cost)})
                    </div>
                    {(detail.materials || []).map(m => (
                      <div key={m.id} style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.8rem', marginBottom: 2 }}>
                        <span>{m.product_name} · {m.display_quantity || m.quantity_used} {m.display_unit || ''}</span>
                        <div>
                          <span style={{ fontWeight: 700, marginRight: 8 }}>{fmt(m.total_cost)}</span>
                          {item.status !== 'completed' && (
                            <button onClick={() => deleteMaterial(m.id)} style={{ background: 'none', border: 'none', color: '#c00', cursor: 'pointer' }}>✕</button>
                          )}
                        </div>
                      </div>
                    ))}
                    {item.status !== 'completed' && (
                      <button onClick={openMaterialModal}
                        style={{ background: 'var(--green-pale)', border: 'none', borderRadius: 6, padding: '4px 10px',
                          fontSize: '0.75rem', fontWeight: 700, cursor: 'pointer', color: 'var(--green-mid)', marginTop: 4 }}>
                        ➕ {t('workOrders.addMaterial')}
                      </button>
                    )}
                  </div>

                  {/* Debrief / summary */}
                  {detail.debrief && (
                    <div style={{ padding: 8, background: '#e8f5e4', borderRadius: 6, fontSize: '0.8rem', marginBottom: 8 }}>
                      <b>{t('workOrders.debrief')}:</b> {detail.debrief}
                    </div>
                  )}

                  <div style={{ fontWeight: 800, fontSize: '0.9rem', marginBottom: 8 }}>
                    {t('common.total')}: {fmt(detail.total_cost)}
                  </div>

                  {item.status !== 'completed' && item.status !== 'cancelled' && (
                    <button onClick={() => setShowCompleteModal(true)}
                      style={{ background: '#27ae60', color: '#fff', border: 'none', borderRadius: 8,
                        padding: '8px 16px', fontSize: '0.82rem', fontWeight: 700, cursor: 'pointer', width: '100%' }}>
                      ✅ {t('workOrders.complete')}
                    </button>
                  )}
                </div>
              )}
            </div>
          ))}
      </div>

      <button className="btn-fab" onClick={() => setShowCreateModal(true)}>+</button>

      {/* Create Work Order Modal */}
      {showCreateModal && (
        <Modal title={t('workOrders.create')} onClose={() => setShowCreateModal(false)}>
          <form onSubmit={handleCreate}>
            <div className="form-group">
              <label>{t('workOrders.titleField')} *</label>
              <input value={form.title} onChange={e => set('title', e.target.value)}
                placeholder="Örn: Kuzey Tarlası Gübreleme" required />
            </div>
            <div className="form-row">
              <div className="form-group">
                <label>{t('workOrders.field')}</label>
                <select value={form.fieldId} onChange={e => set('fieldId', e.target.value)}>
                  <option value="">Seçin</option>
                  {fields.map(f => <option key={f.id} value={f.id}>{f.name}</option>)}
                </select>
              </div>
              <div className="form-group">
                <label>{t('workOrders.workType')}</label>
                <input value={form.workType} onChange={e => set('workType', e.target.value)}
                  placeholder="Gübreleme, Sulama..." />
              </div>
            </div>
            <div className="form-group">
              <label>{t('workOrders.plannedDate')}</label>
              <input type="date" value={form.plannedDate} onChange={e => set('plannedDate', e.target.value)} />
            </div>
            <label style={{ fontSize: '0.8rem', fontWeight: 600, color: '#555', display: 'block', marginBottom: 2 }}>🌾 Sezon</label>
            <SeasonSelector value={form.seasonId} onChange={v => set('seasonId', v)} />
            <div style={{ display: 'flex', gap: 10 }}>
              <button type="button" className="btn" style={{ flex: 1, justifyContent: 'center', background: '#f0f0f0' }}
                onClick={() => setShowCreateModal(false)}>{t('common.cancel')}</button>
              <button type="submit" className="btn btn-primary" style={{ flex: 2, justifyContent: 'center' }} disabled={saving}>
                {saving ? t('common.loading') : t('common.save')}
              </button>
            </div>
          </form>
        </Modal>
      )}

      {/* Add Labour Modal */}
      {showLabourModal && (
        <Modal title={t('workOrders.addLabour')} onClose={() => setShowLabourModal(false)}>
          <form onSubmit={addLabour}>
            <MasterSelect type="contact" filter={{ type: 'labour' }} label={t('labour.fields.worker')}
              value={labourForm.workerName} onChange={v => setLabourForm(f => ({ ...f, workerName: v }))}
              placeholder="İsim..." required />
            <div className="form-group">
              <label>{t('workOrders.workType')}</label>
              <input value={labourForm.workType} onChange={e => setLabourForm(f => ({ ...f, workType: e.target.value }))} />
            </div>
            <div className="form-row">
              <div className="form-group">
                <label>Kişi</label>
                <input type="number" min="1" value={labourForm.numWorkers}
                  onChange={e => setLabourForm(f => ({ ...f, numWorkers: e.target.value }))} />
              </div>
              <div className="form-group">
                <label>Gün</label>
                <input type="number" step="0.5" value={labourForm.days}
                  onChange={e => setLabourForm(f => ({ ...f, days: e.target.value }))} />
              </div>
            </div>
            <div className="form-group">
              <label>{t('labour.fields.dailyRate')}</label>
              <input type="number" step="0.01" value={labourForm.dailyRate}
                onChange={e => setLabourForm(f => ({ ...f, dailyRate: e.target.value }))} placeholder="0.00" />
            </div>
            <div style={{ display: 'flex', gap: 10 }}>
              <button type="button" className="btn" style={{ flex: 1, justifyContent: 'center', background: '#f0f0f0' }}
                onClick={() => setShowLabourModal(false)}>{t('common.cancel')}</button>
              <button type="submit" className="btn btn-primary" style={{ flex: 2, justifyContent: 'center' }}>
                {t('common.save')}
              </button>
            </div>
          </form>
        </Modal>
      )}

      {/* Add Material Modal */}
      {showMaterialModal && (
        <Modal title={t('workOrders.addMaterial')} onClose={() => setShowMaterialModal(false)}>
          <form onSubmit={addMaterial}>
            <div className="form-group">
              <label>{t('workOrders.selectFromStock')}</label>
              <select value={materialForm.stockId} onChange={e => selectStock(e.target.value)}>
                <option value="">Stoktan seçin...</option>
                {stockItems.filter(s => parseFloat(s.quantity) > 0).map(s => (
                  <option key={s.id} value={s.id}>
                    {s.product_name} ({new Intl.NumberFormat('tr-TR').format(s.quantity)} {s.display_unit || s.unit})
                  </option>
                ))}
              </select>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label>Miktar *</label>
                <input type="number" step="0.01" value={materialForm.quantityUsed}
                  onChange={e => setMaterialForm(f => ({ ...f, quantityUsed: e.target.value }))}
                  placeholder="0" required />
              </div>
              <div className="form-group">
                <label>Birim</label>
                <input value={materialForm.displayUnit} readOnly style={{ background: '#f0f0f0' }} />
              </div>
            </div>
            <div style={{ display: 'flex', gap: 10 }}>
              <button type="button" className="btn" style={{ flex: 1, justifyContent: 'center', background: '#f0f0f0' }}
                onClick={() => setShowMaterialModal(false)}>{t('common.cancel')}</button>
              <button type="submit" className="btn btn-primary" style={{ flex: 2, justifyContent: 'center' }}>
                {t('common.save')}
              </button>
            </div>
          </form>
        </Modal>
      )}

      {/* Complete Modal */}
      {showCompleteModal && (
        <Modal title={t('workOrders.complete')} onClose={() => setShowCompleteModal(false)}>
          <div style={{ marginBottom: 12 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.85rem', marginBottom: 4 }}>
              <span>{t('workOrders.labour')}:</span><span style={{ fontWeight: 700 }}>{fmt(detail?.total_labour_cost)}</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.85rem', marginBottom: 4 }}>
              <span>{t('workOrders.materials')}:</span><span style={{ fontWeight: 700 }}>{fmt(detail?.total_material_cost)}</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '1rem', fontWeight: 800, borderTop: '1px solid #ddd', paddingTop: 6 }}>
              <span>{t('common.total')}:</span><span>{fmt(detail?.total_cost)}</span>
            </div>
          </div>
          <div className="form-group">
            <label>{t('workOrders.debrief')}</label>
            <textarea value={debrief} onChange={e => setDebrief(e.target.value)} rows={3}
              placeholder="Tamamlama notları..." />
          </div>
          <button onClick={handleComplete}
            className="btn btn-primary" style={{ width: '100%', justifyContent: 'center' }}>
            ✅ {t('workOrders.complete')}
          </button>
        </Modal>
      )}
    </div>
  );
}
